import React, {FC, Fragment, ReactNode} from 'react';
import styled from 'styled-components';

import {
  KeyboardActionsEnum,
  KeyboardMapping,
  KeyboardShortcut,
} from '../../../../helpers/src/browser/keyboardHelpers';
import {intersperse} from '../../../../helpers/src/utilities/arrayHelpers';
import {fonts, fontSizes} from '../../styles/fontStyles';
import {KeyboardContext} from './keyboardContext';
import {keyRenderers, splitAndFixKeys} from './keyboardShortcutFormatterHelpers';

/*
 * Props.
 */

export interface KeyboardShortcutsFormatterProps {
  /** The keyboard action to display. */
  action: KeyboardActionsEnum;
}

/*
 * Style.
 */

const StyledKeySpan = styled.span`
  font-family: ${fonts.monospace};
  font-size: ${fontSizes.tiny};
  font-style: normal;
  padding: 4px 5px;
  border: 1px solid ${(p) => p.theme.greys.shade50};
  border-radius: 3px;
  box-shadow: inset 0 -1px 0 ${(p) => p.theme.greys.shade40};
  color: ${(p) => p.theme.greys.shade80};
  display: inline-block;
  text-transform: capitalize;
  margin: 0px 2px;
`;

const StyledRow = styled.div`
  padding: 4px 0px;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};

  &:first-of-type {
    border-top: 0px solid ${(p) => p.theme.greys.shade30};
  }
`;

/*
 * Component.
 */

export const KeyboardShortcutsFormatter: FC<KeyboardShortcutsFormatterProps> = (props) => (
  <KeyboardContext.Consumer>
    {(context) => renderKeyboardShortcuts(context.mapping, props.action)}
  </KeyboardContext.Consumer>
);

/** Render the shortcuts for the specified action and mapping. */
function renderKeyboardShortcuts(mapping: KeyboardMapping, action: KeyboardActionsEnum) {
  return <>{mapping[action].map(renderKeyboardShortcutRow)}</>;
}

/** Render a single shortcut. */
function renderKeyboardShortcutRow(shortcut: KeyboardShortcut) {
  // Split into the sequences of keys that need to be pressed
  const shortcutKeys = shortcut.sequence.split(' ');

  // Render, separating each sequence with a space
  return <StyledRow key={shortcut.sequence}>{shortcutKeys.map(renderKeyboardShortcutKeys)}</StyledRow>;
}

/** Render a group of keys. */
function renderKeyboardShortcutKeys(shortcut: string) {
  const fixedKeys = splitAndFixKeys(shortcut);

  // Find the correct translation for each.
  const translatedKeys = fixedKeys.map((k) => {
    const renderer = keyRenderers[k];
    return <StyledKeySpan key={k}>{renderer ? renderer() : k}</StyledKeySpan>;
  });

  // Render, separated by "+" signs.
  return <Fragment key={shortcut}>{intersperse<ReactNode>(translatedKeys, '+')}</Fragment>;
}
